import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  ArrowLeft, Pencil, QrCode, Copy, Download, Loader2, Plus, Info,
  ClipboardList, Key,
} from 'lucide-react'
import { apiClient } from '@/api/client'
import {
  useLibro,
  useLibroStats,
  useCodigos,
  useCampos,
  useRegistros,
  useGenerarCodigos,
  useToggleCodigoStatus,
  useDeleteCodigo,
} from '../../hooks/useQrCodigo'
import { getExportarRegistrosUrl, getExportarCodigosUrl, getDescargarQRUrl } from '../../api/qr-codigo'
import { StatsCards } from '../../components/StatsCards'
import { CodigosTable } from '../../components/CodigosTable'
import { RegistrosTable } from '../../components/RegistrosTable'
import { ActividadDiariaChart } from '../../components/ActividadDiariaChart'
import { DistribucionCodigosChart } from '../../components/DistribucionCodigosChart'

export function LibroDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const libroId = id ? parseInt(id) : undefined

  const { data: libro, isLoading } = useLibro(libroId)
  const { data: stats } = useLibroStats(libroId)
  const { data: codigos = [] } = useCodigos(libroId)
  const { data: campos = [] } = useCampos(libroId)
  const { data: registros = [] } = useRegistros(libroId)

  const generarMutation = useGenerarCodigos()
  const toggleCodigoMutation = useToggleCodigoStatus()
  const deleteCodigoMutation = useDeleteCodigo()

  const [cantidad, setCantidad] = useState(10)
  const [showInfo, setShowInfo] = useState(false)

  const handleGenerar = () => {
    if (!libroId || cantidad < 1 || cantidad > 1000) return
    generarMutation.mutate({ libroId, cantidad })
  }

  const handleExport = async (url: string) => {
    const response = await apiClient.get(url, { responseType: 'blob' })
    const blob = new Blob([response.data])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    const disposition = response.headers['content-disposition'] || ''
    const filenameMatch = disposition.match(/filename="?(.+?)"?$/)
    link.download = filenameMatch ? filenameMatch[1] : 'export.xlsx'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const copyPublicUrl = () => {
    if (!libro) return
    const url = `https://enterprise.tonalstrategies.com/qr/${libro.uuid}`
    const textarea = document.createElement('textarea')
    textarea.value = url
    textarea.style.cssText = 'position:fixed;left:0;top:0;opacity:0;'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    let copied = false
    try {
      copied = document.execCommand('copy')
    } catch {
      copied = false
    }
    document.body.removeChild(textarea)
    if (copied) {
      toast.success('URL copiada al portapapeles')
    } else {
      toast.info(`URL: ${url}`, { duration: 10000 })
    }
  }

  if (isLoading || !libro) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/libro-qr')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{libro.nombre}</h1>
              <Badge className={libro.activo ? 'badge-gradient' : 'bg-gray-500 text-white'}>
                {libro.activo ? 'Activo' : 'Inactivo'}
              </Badge>
              {libro.descripcion && (
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Info className="h-5 w-5" />
                </button>
              )}
            </div>
            {showInfo && libro.descripcion && (
              <p className="text-sm text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-200 mt-1">{libro.descripcion}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyPublicUrl} title="Copiar URL público">
            <Copy className="mr-2 h-4 w-4" /> URL
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport(getDescargarQRUrl(libro.id))}
            title="Descargar QR"
          >
            <QrCode className="mr-2 h-4 w-4" /> QR
          </Button>
          <Button
            size="sm"
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={() => navigate(`/libro-qr/${libro.id}/editar`)}
          >
            <Pencil className="mr-2 h-4 w-4" /> Editar
          </Button>
        </div>
      </div>

      {/* Resumen General */}
      {stats && <StatsCards stats={stats} />}

      {/* Gráficos */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2">
          <DistribucionCodigosChart stats={stats} />
          {stats.stats_diarios.length > 0 && (
            <ActividadDiariaChart data={stats.stats_diarios} />
          )}
        </div>
      )}

      {/* Registros */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-teal-500" />
            Registros
            <Badge className="badge-gradient">{registros.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RegistrosTable
            registros={registros}
            campos={campos}
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport(getExportarRegistrosUrl(libro.id))}
              >
                <Download className="mr-2 h-4 w-4" /> Exportar Excel
              </Button>
            }
          />
        </CardContent>
      </Card>

      {/* Códigos */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="h-5 w-5 text-teal-500" />
            Códigos
            <Badge className="badge-gradient">{codigos.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CodigosTable
            codigos={codigos}
            onToggle={(codigoId) => toggleCodigoMutation.mutate({ libroId: libro.id, codigoId })}
            onDelete={(codigoId) => {
              if (confirm('¿Eliminar este código?')) {
                deleteCodigoMutation.mutate({ libroId: libro.id, codigoId })
              }
            }}
            actions={
              <>
                <Input
                  type="number"
                  value={cantidad}
                  onChange={(e) => setCantidad(parseInt(e.target.value) || 0)}
                  min={1}
                  max={1000}
                  className="w-24"
                />
                <Button
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                  onClick={handleGenerar}
                  disabled={generarMutation.isPending}
                >
                  {generarMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Generar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport(getExportarCodigosUrl(libro.id))}
                >
                  <Download className="mr-2 h-4 w-4" /> Excel
                </Button>
              </>
            }
          />
        </CardContent>
      </Card>
    </div>
  )
}
